import { useState } from 'react'
import dayjs, { type Dayjs } from 'dayjs'

export const useCustomsDate = () => {
  const startOfToday = dayjs().startOf('day')
  const endOfToday = dayjs().endOf('day')
  const sevenDaysAgo = startOfToday.subtract(7, 'day')

  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs>(sevenDaysAgo)
  const [selectedToDate, setSelectedToDate] = useState<Dayjs>(endOfToday)

  const unixFromDate = selectedFromDate.valueOf()
  const unixToDate = selectedToDate.valueOf()

  /**
   * Clamps the given date to today if it is null or after today, otherwise returns the start of the day for the given date.
   * @param date The date to clamp. If null, it will be clamped to today.
   * @returns The clamped date, which is either today or the start of the day for the given date.
   */
  const clamp = (date: Dayjs | null): Dayjs => {
    if (!date || date.isAfter(startOfToday)) {
      return startOfToday
    }

    return date.startOf('day')
  }

  /**
   * Handles the change event for the "from" date input. It clamps the new date and updates the state. If the new "from" date is after the current "to" date, it also updates the "to" date to match the new "from" date.
   * @param date The new date selected for the "from" date input. If null, it will be clamped to today.
   */
  const onFromChange = (date: Dayjs | null) => {
    const newFrom = clamp(date)
    setSelectedFromDate(newFrom)

    if (selectedToDate.isBefore(newFrom)) {
      setSelectedToDate(newFrom.endOf('day'))
    }
  }

  /**
   * Handles the change event for the "to" date input. It clamps the new date and updates the state. If the current "from" date is after the new "to" date, it also updates the "from" date to match the new "to" date.
   * @param date The new date selected for the "to" date input. If null, it will be clamped to today.
   */
  const onToChange = (date: Dayjs | null) => {
    const newTo = clamp(date)
    setSelectedToDate(newTo.endOf('day'))

    if (selectedFromDate.isAfter(newTo)) {
      setSelectedFromDate(newTo)
    }
  }

  return {
    today: startOfToday,
    from: {
      selectedDate: selectedFromDate,
      unix: unixFromDate,
      onChange: onFromChange,
    },
    to: {
      selectedDate: selectedToDate,
      unix: unixToDate,
      onChange: onToChange,
    },
  }
}
